<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <examination-form
    v-else
    :key="initExamination.id"
    :initExamination="initExamination"
    :initCustomer="initCustomer"
    :successMsg="$t('actions.saveChangesSuccess')"
    :submitAction="update"
    :enable-print="true"
  />
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref, watch } from 'vue'

import { useHTTP } from '@/plugins/http'

import Customer from '@/models/customer/Customer'

// components
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ExaminationForm from '@/views/examinations/ExaminationForm.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { useRoute, useRouter } from 'vue-router'
import Examination, { type ExamType } from '@/models/customer/Examination'

export default defineComponent({
  name: 'examination',
  components: {
    LoadingSpinner,
    ExaminationForm,
    AlertBadge
  },
  setup() {
    const initExamination = ref(new Examination())
    const initCustomer = ref(new Customer())
    const error = ref({ title: '', body: '' })
    const http = useHTTP()
    const loading = ref(true)
    const route = useRoute()
    const router = useRouter()

    const load = async () => {
      loading.value = true
      const url = `${Examination.ENDPOINT}/${route.params.examination_id}`

      try {
        const resp = await http.get<Examination>(url)
        initExamination.value = Examination.from(resp)
        if (initExamination.value.customer) {
          initCustomer.value = initExamination.value.customer
        }
      } catch (errorResp) {
        error.value.title = errorResp.title
        error.value.body = errorResp.body
      }

      loading.value = false
    }

    onBeforeMount(load)
    // Picking another exam from the history drawer stays on this page with a new id
    watch(() => route.params.examination_id, (id, oldID) => {
      if (id && id !== oldID && route.name === 'examination') load()
    })

    const update = async (examination: Examination, print = false, examType?: ExamType) => {
      try {
        const url = `${Examination.ENDPOINT}/${examination.id}`
        const resp = await http.put<Examination>(url, examination)
        initExamination.value = Examination.from(resp)
        if (print) {
          await router.replace({
            name: 'examination-receipt',
            params: { examination_id: initExamination.value.id },
            query: examType ? { exam_type: examType } : undefined
          })
        } else {
          router.back()
        }
      } catch (error) {
        return error
      }
    }

    return {
      initCustomer,
      initExamination,
      error,
      loading,
      update
    }
  }
})
</script>
